import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, Button, Modal, Space, Typography, message } from "antd";
import { CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import type { Company } from "../types/company";
import {
  LeanCustomsService,
  type LeanCustomsOrganization,
  type LeanCustomsContract,
  type LeanCustomsContractSigner,
  type LeanCustomsContractSupportDocument,
} from "../services/leancustomsService";
import { LeanCustomsIntegrationDetailsContent } from "../pages/Customs/LeanCustomsIntegrationDetailsContent";

const { Text } = Typography;

export interface LeanCustomsIntegrationStripAndModalProps {
  /** Must include `leancustoms_organization_id` and a numeric `id` for API calls. */
  company: Company;
  /** Increment to refetch organization and contracts. */
  refreshTrigger?: number;
  style?: React.CSSProperties;
}

/**
 * Same UX as /customs for customers: one compact row + modal with org & contract details.
 */
export const LeanCustomsIntegrationStripAndModal: React.FC<
  LeanCustomsIntegrationStripAndModalProps
> = ({ company, refreshTrigger = 0, style }) => {
  const { t } = useTranslation();
  const companyId = company.id;
  const orgId = company.leancustoms_organization_id;

  const [modalVisible, setModalVisible] = useState(false);
  const [organization, setOrganization] =
    useState<LeanCustomsOrganization | null>(null);
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [contracts, setContracts] = useState<LeanCustomsContract[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [contractSigners, setContractSigners] = useState<
    Record<string, LeanCustomsContractSigner[]>
  >({});
  const [loadingSigners, setLoadingSigners] = useState<Record<string, boolean>>(
    {}
  );
  const [contractSupportDocuments, setContractSupportDocuments] = useState<
    Record<string, LeanCustomsContractSupportDocument[]>
  >({});
  const [loadingSupportDocuments, setLoadingSupportDocuments] = useState<
    Record<string, boolean>
  >({});

  const signersRequestedRef = useRef<Set<string>>(new Set());
  const supportDocsRequestedRef = useRef<Set<string>>(new Set());

  const loadOrganization = useCallback(async () => {
    if (!orgId || !companyId) return;
    setLoadingOrg(true);
    try {
      const result = await LeanCustomsService.getOrganization(orgId, companyId);
      if (result.success && result.data) {
        setOrganization(result.data);
      } else {
        message.error(
          result.error || t("customs.errors.failedToLoadOrganization")
        );
      }
    } catch {
      message.error(t("customs.errors.failedToLoadOrganization"));
    } finally {
      setLoadingOrg(false);
    }
  }, [orgId, companyId, t]);

  const loadContracts = useCallback(async () => {
    if (!orgId || !companyId) return;
    setLoadingContracts(true);
    try {
      const result = await LeanCustomsService.listOrganizationContracts(
        orgId,
        companyId,
        { page: 1, page_size: 100 }
      );
      if (result.success && result.data) {
        setContracts(result.data.results);
      } else {
        message.error(
          result.error || t("customs.errors.failedToLoadContracts")
        );
      }
    } catch {
      message.error(t("customs.errors.failedToLoadContracts"));
    } finally {
      setLoadingContracts(false);
    }
  }, [orgId, companyId, t]);

  useEffect(() => {
    if (!orgId || !companyId) return;
    signersRequestedRef.current.clear();
    supportDocsRequestedRef.current.clear();
    setContractSigners({});
    setContractSupportDocuments({});
    void loadOrganization();
    void loadContracts();
  }, [orgId, companyId, refreshTrigger, loadOrganization, loadContracts]);

  const loadContractSigners = async (contractId: string) => {
    if (!companyId) return;
    if (signersRequestedRef.current.has(contractId)) return;
    signersRequestedRef.current.add(contractId);

    setLoadingSigners((prev) => ({ ...prev, [contractId]: true }));
    try {
      const result = await LeanCustomsService.listContractSigners(
        contractId,
        companyId,
        { page: 1, page_size: 100 }
      );
      if (result.success && result.data) {
        setContractSigners((prev) => ({
          ...prev,
          [contractId]: result.data.results,
        }));
      }
    } catch (e) {
      console.error("Error loading contract signers:", e);
    } finally {
      setLoadingSigners((prev) => ({ ...prev, [contractId]: false }));
    }
  };

  const loadContractSupportDocuments = async (contractId: string) => {
    if (!companyId) return;
    if (supportDocsRequestedRef.current.has(contractId)) return;
    supportDocsRequestedRef.current.add(contractId);

    setLoadingSupportDocuments((prev) => ({ ...prev, [contractId]: true }));
    try {
      const result = await LeanCustomsService.listContractSupportDocuments(
        contractId,
        companyId
      );
      if (result.success && result.data) {
        setContractSupportDocuments((prev) => ({
          ...prev,
          [contractId]: result.data.results,
        }));
      }
    } catch (e) {
      console.error("Error loading contract support documents:", e);
    } finally {
      setLoadingSupportDocuments((prev) => ({ ...prev, [contractId]: false }));
    }
  };

  const handleContractPanelsChange = (activeKeys: string | string[]) => {
    const keys = Array.isArray(activeKeys) ? activeKeys : [activeKeys];
    keys.forEach((contractId) => {
      if (!contractId) return;
      void loadContractSigners(contractId);
      void loadContractSupportDocuments(contractId);
    });
  };

  const handleDownloadSupportDocument = async (
    contractId: string,
    documentId: string,
    filename: string
  ) => {
    if (!companyId) return;
    try {
      const blob = await LeanCustomsService.downloadContractSupportDocument(
        contractId,
        documentId,
        companyId
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success(t("customs.documentDownloaded"));
    } catch (e) {
      console.error("Error downloading document:", e);
      message.error(t("customs.errors.failedToDownloadDocument"));
    }
  };

  const handleDownloadSignerDocument = async (
    contractId: string,
    signerId: string,
    signerName: string
  ) => {
    if (!companyId) return;
    try {
      const blob = await LeanCustomsService.downloadContractSignerDocument(
        contractId,
        signerId,
        companyId
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `signer-document-${signerName.replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success(t("customs.documentDownloaded"));
    } catch (e) {
      console.error("Error downloading signer document:", e);
      message.error(t("customs.errors.failedToDownloadDocument"));
    }
  };

  if (!orgId || !companyId) {
    return null;
  }

  return (
    <>
      <Card
        size="small"
        loading={loadingOrg}
        style={{ marginBottom: 16, ...style }}
        styles={{ body: { padding: "12px 16px" } }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <Space align="start" size={12}>
            <CheckCircleOutlined
              style={{ color: "#52c41a", fontSize: 22, marginTop: 2 }}
            />
            <div>
              <div style={{ fontWeight: 600 }}>
                {t("customs.connectedToLeanCustoms")}
              </div>
              {organization?.name ? (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {organization.name}
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t("customs.connectedStripNoOrgName")}
                </Text>
              )}
            </div>
          </Space>
          <Button
            type="default"
            icon={<InfoCircleOutlined />}
            onClick={() => setModalVisible(true)}
          >
            {t("customs.viewIntegrationDetails")}
          </Button>
        </div>
      </Card>

      <Modal
        title={t("customs.integrationDetailsModalTitle")}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setModalVisible(false)}>
            {t("common.close")}
          </Button>,
        ]}
        width={920}
        styles={{
          body: { maxHeight: "min(78vh, 720px)", overflowY: "auto" },
        }}
      >
        <LeanCustomsIntegrationDetailsContent
          organization={organization}
          contracts={contracts}
          loadingContracts={loadingContracts}
          contractSigners={contractSigners}
          loadingSigners={loadingSigners}
          contractSupportDocuments={contractSupportDocuments}
          loadingSupportDocuments={loadingSupportDocuments}
          onContractPanelsChange={handleContractPanelsChange}
          onDownloadSignerDocument={handleDownloadSignerDocument}
          onDownloadSupportDocument={handleDownloadSupportDocument}
        />
      </Modal>
    </>
  );
};
